import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { TopologySnapshot } from '@tiw/shared-types';

export interface TopologySnapshotState {
  readonly snapshot: TopologySnapshot | null;
  readonly loading: boolean;
  readonly error: Error | null;
}

/**
 * Fetch the global topology snapshot once on mount.
 *
 * Served by the edge Worker from D1 (`/v1/topology/snapshot`). See
 * docs/API_CONTRACTS.md and docs/BACKEND_PLAN.md.
 */
export function useTopologySnapshot(): TopologySnapshotState {
  const [snapshot, setSnapshot] = useState<TopologySnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api
      .get<TopologySnapshot>('/v1/topology/snapshot', { signal: controller.signal })
      .then((data) => {
        setSnapshot(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  return { snapshot, loading, error };
}
